import React, { useId, useLayoutEffect, useMemo, useState } from "react";
import "./FirefliesEffect.scss";

const FIREFLY_COUNT = 34;
/** Near-maximum stacking so the overlay stays above host UI; layer uses `pointer-events: none`. */
const FIREFLIES_Z = 2147483646;

function useViewportSize(): { vw: number; vh: number } {
  const [size, setSize] = useState(() => ({
    vw: window.innerWidth,
    vh: window.innerHeight,
  }));

  useLayoutEffect(() => {
    const onResize = () => {
      setSize({ vw: window.innerWidth, vh: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return size;
}

export interface FirefliesEffectProps {
  className?: string;
  children?: React.ReactNode;
}

export default function FirefliesEffect(props: FirefliesEffectProps): React.JSX.Element {
  const { className } = props;
  const safeId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const rootClassName = ["fireflies-effect", className].filter(Boolean).join(" ");
  const { vw, vh } = useViewportSize();

  const dynamicCss = useMemo(() => {
    const random = (max: number) => Math.floor(Math.random() * max);
    const getProportion = (sizeRef: number, size: number) => Math.floor((sizeRef * size) / 100);
    const getRandWidth = () => getProportion(vw, random(100) - 50);
    const getRandHeight = () => getProportion(vh, random(100) - 50);

    let css = "";
    for (let i = 1; i <= FIREFLY_COUNT; i++) {
      const step = random(12) + 16;
      const speed = random(10) + 8;
      const moveName = `fireflies-move-${safeId}-${i}`;
      css += `.fireflies-effect[data-fireflies-id="${safeId}"] .fireflies-effect__flies:nth-child(${i}) {
          animation-name: ${moveName};
        }
        .fireflies-effect[data-fireflies-id="${safeId}"] .fireflies-effect__flies:nth-child(${i})::before {
          animation-duration: ${speed}s;
        }
        .fireflies-effect[data-fireflies-id="${safeId}"] .fireflies-effect__flies:nth-child(${i})::after {
          animation-duration: ${speed}s, ${random(6000) + 5000}ms;
          animation-delay: 0ms, ${random(8000) + 500}ms;
        }
        @keyframes ${moveName} {`;
      for (let j = 0; j < step; j++) {
        css += `${j * (100 / step)}% {
          transform: translateX(${getRandWidth()}px) translateY(${getRandHeight()}px) scale(${random(75) / 100 + 0.25});
          }`;
      }
      css += "}";
    }
    return css;
  }, [vw, vh, safeId]);

  return (
    <div
      className={rootClassName}
      data-fireflies-id={safeId}
      style={
        {
          // "--fly-size": `${refSize * 0.0013}px`,
          // "--fly-blur": `${refSize * 0.034}px`,
          // "--fly-spread": `${refSize * 0.0055}px`,
          // "--fly-visibility": `-${refSize * 0.1}px`,
          "--fly-size": `3.4px`,
          "--fly-blur": `72px`,
          "--fly-spread": `13px`,
          "--fly-visibility": `-272px`,
          "--fireflies-z": FIREFLIES_Z,
        } as React.CSSProperties
      }
    >
      <style>{dynamicCss}</style>
      <div className="fireflies-effect__layer" aria-hidden>
        <div className="fireflies-effect__content">
          {Array.from({ length: FIREFLY_COUNT }, (_, i) => (
            <div key={i} className="fireflies-effect__flies" />
          ))}
        </div>
      </div>
    </div>
  );
}
